import React from 'react';
import { Vote, Calendar, User, Trophy, ArrowRight, CheckCircle2, Info, Plus, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { RichContentViewer } from '../components/RichContentViewer';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../hooks/usePermissions';
import { useElections, useCandidates, useVote, useNominate, useCreateElection } from '../hooks/useElections';
import { motion, AnimatePresence } from 'motion/react';
import { safeFormat, cn } from '../lib/utils';
import { Election, Candidate } from '../types';
import toast from 'react-hot-toast';

export const ElectionsPage = () => {
  const { node, brand, getTerm } = useTenant();
  const { can } = usePermissions();
  const { data: elections = [], isLoading: isLoadingElections } = useElections(node?.id || '', 'ELECTION');
  const [selectedElectionId, setSelectedElectionId] = React.useState<string | null>(null);
  const [selectedCandidate, setSelectedCandidate] = React.useState<Candidate | null>(null);
  const [isNominationModalOpen, setIsNominationModalOpen] = React.useState(false);
  const [isCreateElectionModalOpen, setIsCreateElectionModalOpen] = React.useState(false);
  const [newElectionTitle, setNewElectionTitle] = React.useState('');
  const [newElectionDescription, setNewElectionDescription] = React.useState('');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [votingFor, setVotingFor] = React.useState<Candidate | null>(null);

  const createElectionMutation = useCreateElection(node?.id || '');

  const handleCreateElection = (e: React.FormEvent) => {
    e.preventDefault();
    createElectionMutation.mutate({
      title: newElectionTitle,
      description: newElectionDescription,
      type: 'ELECTION',
      startDate: startDate,
      endDate: endDate,
      isAnonymous: true,
      allowSelfNomination: true,
      roles: ['Board Member', 'Treasurer', 'President'],
      options: []
    }, {
      onSuccess: () => {
        setIsCreateElectionModalOpen(false);
        setNewElectionTitle('');
        setNewElectionDescription('');
        setStartDate('');
        setEndDate('');
      }
    });
  };
  
  // State for nomination form
  const [nominationData, setNominationData] = React.useState({
    role: '',
    statement: '',
    platform: ''
  });

  const activeElections = elections.filter(e => e.status === 'ACTIVE');
  const upcomingElections = elections.filter(e => e.status === 'UPCOMING');
  const concludedElections = elections.filter(e => e.status === 'CONCLUDED');

  const currentElection = elections.find(e => e.id === selectedElectionId) || activeElections[0];
  const { data: candidates = [], isLoading: isLoadingCandidates } = useCandidates(currentElection?.id || '');
  
  const voteMutation = useVote(currentElection?.id || '');
  const nominateMutation = useNominate(currentElection?.id || '');

  // Auto-select the first active election when the list loads (render-phase adjustment).
  const firstElectionId = activeElections[0]?.id ?? null;
  const [prevAutoElectionId, setPrevAutoElectionId] = React.useState<string | null>(null);
  if (firstElectionId && !selectedElectionId && firstElectionId !== prevAutoElectionId) {
    setPrevAutoElectionId(firstElectionId);
    setSelectedElectionId(firstElectionId);
  }

  const handleVote = () => {
    if (!votingFor) return;
    voteMutation.mutate(votingFor.id, {
      onSuccess: () => {
        setVotingFor(null);
      }
    });
  };

  const handleNominate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentElection) return;
    nominateMutation.mutate({
      ...nominationData,
      electionId: currentElection.id,
    }, {
      onSuccess: () => {
        setIsNominationModalOpen(false);
        setNominationData({ role: '', statement: '', platform: '' });
      }
    });
  };

  if (isLoadingElections) {
    return <div className="flex items-center justify-center h-64 font-mono text-xs uppercase tracking-widest text-gray-400">Synchronizing Governance State...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-12">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Election Center</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Democratic participation in {node?.name} leadership.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {currentElection?.allowSelfNomination && can('cast_vote') && (
            <Button variant="ghost" className="gap-2 border-2 border-black dark:border-white" onClick={() => setIsNominationModalOpen(true)}>
              <Plus size={18} />
              Self-Nominate
            </Button>
          )}
          {can('manage_elections') && (
            <Button className="gap-2" style={{ backgroundColor: brand.primary }} onClick={() => setIsCreateElectionModalOpen(true)}>
              <Plus size={18} />
              Create Election
            </Button>
          )}
        </div>
      </header>

      {/* Election Selector Tab-like UI */}
      {elections.length > 1 && (
        <div className="flex overflow-x-auto pb-2 gap-2 no-scrollbar">
          {elections.map((election) => (
            <button
              key={election.id}
              onClick={() => setSelectedElectionId(election.id)}
              className={cn(
                "px-4 py-2 rounded-none text-xs font-bold uppercase tracking-widest transition-all whitespace-nowrap",
                selectedElectionId === election.id
                  ? "bg-navy-900 text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
                  : "bg-white text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:bg-slate-800"
              )}
            >
              {election.title}
              {election.status === 'ACTIVE' && <span className="ml-2 w-2 h-2 rounded-none bg-green-500 inline-block animate-pulse" />}
            </button>
          ))}
        </div>
      )}

      {currentElection ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-brand-primary/5 border-brand-primary/20" style={{ backgroundColor: `${brand.primary}08`, borderColor: `${brand.primary}20` }}>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-none text-white" style={{ backgroundColor: brand.primary }}>
                  <Vote size={24} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">{currentElection.title}</h2>
                    <Badge variant={currentElection.status === 'ACTIVE' ? 'green' : currentElection.status === 'UPCOMING' ? 'blue' : 'gray'}>
                      {currentElection.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{currentElection.description}</p>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={14} />
                      {currentElection.status === 'UPCOMING' ? 'Starts: ' : 'Ends: '}
                      {safeFormat(currentElection.status === 'UPCOMING' ? currentElection.startDate : currentElection.endDate, 'MM/dd/yyyy')}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <User size={14} />
                      {candidates.length} Candidates
                    </div>
                    {currentElection.isAnonymous && (
                      <div className="flex items-center gap-1.5 text-brand-primary font-bold">
                        <ShieldCheck size={14} />
                        Anonymous Ballot
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                Candidates
                <Badge variant="gray">{candidates.length}</Badge>
              </h3>
              
              {isLoadingCandidates ? (
                <div className="h-32 flex items-center justify-center text-xs text-gray-400 font-mono animate-pulse">Loading Nominees...</div>
              ) : (
                <div className="grid gap-4">
                  {candidates.map((candidate) => (
                    <Card key={candidate.id} className="relative overflow-hidden group">
                      <div className="flex flex-col md:flex-row gap-6">
                        <div className="w-full md:w-32 h-32 md:h-auto bg-white rounded-none overflow-hidden flex-shrink-0 flex items-center justify-center text-gray-400 group-hover:scale-105 transition-transform duration-500 dark:bg-slate-800">
                          {candidate.photoUrl ? (
                            <img src={candidate.photoUrl} alt={candidate.name} className="w-full h-full object-cover" />
                          ) : (
                            <User size={48} />
                          )}
                        </div>
                        <div className="flex-1 flex flex-col py-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="text-lg font-bold text-gray-900 dark:text-white">{candidate.name}</h4>
                              <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: brand.primary }}>Running for {candidate.role}</p>
                            </div>
                            {currentElection.status === 'CONCLUDED' && !currentElection.isAnonymous && candidate.votesReceived !== undefined && (
                              <div className="text-right">
                                <span className="text-2xl font-black text-gray-900 dark:text-white">{candidate.votesReceived}</span>
                                <p className="text-[8px] font-black uppercase text-gray-400 tracking-tighter">Total Votes</p>
                              </div>
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mb-4 leading-relaxed">
                            "{candidate.statement}"
                          </p>

                          <div className="mt-auto flex items-center justify-between">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-xs font-bold gap-1 p-0 hover:bg-transparent"
                              onClick={() => setSelectedCandidate(candidate)}
                            >
                              Read Full Platform <ArrowRight size={14} />
                            </Button>
                            
                            {currentElection.status === 'ACTIVE' && can('cast_vote') && (
                              <Button 
                                size="sm" 
                                className="h-8 rounded-none px-6"
                                onClick={() => setVotingFor(candidate)}
                                style={{ backgroundColor: brand.primary }}
                                disabled={voteMutation.isPending}
                              >
                                {voteMutation.isPending && votingFor?.id === candidate.id ? 'Casting...' : 'Vote'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="space-y-6">
            <Card className="bg-navy-900 text-white border-none shadow-2xl">
              <h4 className="font-bold mb-4 flex items-center gap-2 text-sm">
                <Info size={16} style={{ color: brand.primary }} />
                Democratic Rules
              </h4>
              <div className="space-y-4">
                <div className="space-y-1">
                  <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: brand.primary }}>Identity Isolation</p>
                  <p className="text-xs text-navy-200">The backend utilizes decoupling tables to ensure your vote identity is never linked to your selection.</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: brand.primary }}>1-Vote Constraint</p>
                  <p className="text-xs text-navy-200">Database-level unique indexes prevent double-voting across all shards.</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: brand.primary }}>Auditability</p>
                  <p className="text-xs text-navy-200">Hash-chains are generated for each ballot to allow for results verification without compromising secrecy.</p>
                </div>
              </div>
            </Card>

            {concludedElections.length > 0 && (
              <Card>
                <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-4 text-sm flex items-center gap-2">
                  <Trophy size={16} className="text-amber-500" />
                  Past Outcomes
                </h4>
                <div className="space-y-3">
                  {concludedElections.map(election => (
                    <div 
                      key={election.id} 
                      className="p-3 bg-white rounded-none border-2 border-black dark:border-white hover:border-black dark:border-white transition-colors cursor-pointer dark:bg-slate-950"
                      onClick={() => setSelectedElectionId(election.id)}
                    >
                      <p className="text-[10px] font-black text-gray-900 dark:text-gray-100 uppercase tracking-widest mb-1">{election.title}</p>
                      <div className="flex justify-between items-end">
                        <span className="text-[10px] text-gray-500">{safeFormat(election.endDate, 'MMM yyyy')}</span>
                        <ArrowRight size={12} className="text-gray-300" />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </aside>
        </div>
      ) : (
        <Card className="flex flex-col items-center justify-center py-24 text-center border-dashed border-2">
          <div className="p-4 bg-white rounded-none text-gray-400 mb-4 dark:bg-slate-800">
            <Vote size={48} />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">No Historical Data</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mt-2">
            Governance activity has not yet begun for this node. Check with leadership for upcoming election cycles.
          </p>
        </Card>
      )}

      {/* Candidate Platform Modal */}
      <Modal
        isOpen={!!selectedCandidate}
        onClose={() => setSelectedCandidate(null)}
        title="Candidate Profile"
        className="max-w-2xl"
      >
        {selectedCandidate && (
          <div className="p-6">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-20 h-20 bg-white rounded-none flex items-center justify-center text-gray-400 dark:bg-slate-800">
                {selectedCandidate.photoUrl ? (
                  <img src={selectedCandidate.photoUrl} alt={selectedCandidate.name} className="w-full h-full object-cover rounded-none" />
                ) : (
                  <User size={40} />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-black font-display text-navy-900 dark:text-white">{selectedCandidate.name}</h2>
                <Badge variant="blue" className="text-[10px] font-black uppercase tracking-widest">{selectedCandidate.role} Candidate</Badge>
              </div>
            </div>

            <div className="bg-white p-6 rounded-none border-2 border-black dark:border-white mb-8 whitespace-pre-wrap dark:bg-slate-950">
              <RichContentViewer content={selectedCandidate.platform || selectedCandidate.statement} />
            </div>

            <div className="flex gap-3 justify-end items-center mt-6">
              <Button variant="secondary" onClick={() => setSelectedCandidate(null)}>Dismiss</Button>
              {currentElection?.status === 'ACTIVE' && can('cast_vote') && (
                <Button 
                  style={{ backgroundColor: brand.primary }}
                  onClick={() => {
                    setVotingFor(selectedCandidate);
                    setSelectedCandidate(null);
                  }}
                >
                  Cast Ballot
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Nomination Modal */}
      <Modal
        isOpen={isNominationModalOpen}
        onClose={() => setIsNominationModalOpen(false)}
        title="Self-Nomination Form"
        className="max-w-xl"
      >
        <form onSubmit={handleNominate} className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1.5 block">Target Role</label>
              <select
                required
                className="w-full h-12 bg-white border-none rounded-none px-4 text-sm font-bold focus:ring-2 focus:ring-brand-primary dark:bg-slate-800"
                value={nominationData.role}
                onChange={(e) => setNominationData({ ...nominationData, role: e.target.value })}
              >
                <option value="">Select a role...</option>
                {currentElection?.roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1.5 block">Short Statement</label>
              <Input
                required
                placeholder="A brief summary for candidate cards..."
                value={nominationData.statement}
                onChange={(e) => setNominationData({ ...nominationData, statement: e.target.value })}
              />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1.5 block">Full Platform</label>
              <Textarea
                required
                rows={8}
                placeholder="Detailed vision, priorities, and why voters should choose you..."
                value={nominationData.platform}
                onChange={(e) => setNominationData({ ...nominationData, platform: e.target.value })}
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-4 border-t border-black dark:border-white">
            <Button variant="secondary" type="button" onClick={() => setIsNominationModalOpen(false)}>Cancel</Button>
            <Button 
              type="submit" 
              style={{ backgroundColor: brand.primary }}
              disabled={nominateMutation.isPending}
            >
              {nominateMutation.isPending ? 'Submitting...' : 'Submit Nomination'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* High-Friction Vote Confirmation Modal */}
      <Modal
        isOpen={!!votingFor}
        onClose={() => setVotingFor(null)}
        title="Confirm Your Vote"
        className="max-w-md"
      >
        <div className="p-8 text-center space-y-6">
          <div className="mx-auto w-16 h-16 bg-brand-primary/10 rounded-none flex items-center justify-center text-brand-primary" style={{ backgroundColor: `${brand.primary}20`, color: brand.primary }}>
            <AlertTriangle size={32} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Final Confirmation</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              You are about to cast your vote for <span className="font-bold text-navy-900 dark:text-white">{votingFor?.name}</span> as <span className="font-bold text-navy-900 dark:text-white">{votingFor?.role}</span>.
              This action <span className="text-red-600 font-bold">cannot be undone</span> once submitted to the vault.
            </p>
          </div>
          <div className="bg-amber-50 border-2 border-black dark:border-white border-amber-100 p-4 rounded-none flex gap-3 text-left">
            <ShieldCheck size={20} className="text-amber-600 flex-shrink-0" />
            <p className="text-[10px] text-amber-700 italic font-medium">
              By clicking "Confirm Vote", you attest that this is your final selection and acknowledge that your member ID will be flagged as "Voted" for this election cycle.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Button 
              size="lg"
              className="w-full font-black uppercase tracking-widest text-sm h-14"
              style={{ backgroundColor: brand.primary }}
              onClick={handleVote}
              disabled={voteMutation.isPending}
            >
              {voteMutation.isPending ? 'Processing Encryption...' : 'Confirm Vote'}
            </Button>
            <Button variant="ghost" onClick={() => setVotingFor(null)}>Return to Candidates</Button>
          </div>
        </div>
      </Modal>

      {/* Create Election Modal */}
      <Modal
        isOpen={isCreateElectionModalOpen}
        onClose={() => setIsCreateElectionModalOpen(false)}
        title="Initialize New Election"
        className="max-w-xl"
      >
        <form onSubmit={handleCreateElection} className="p-6 space-y-6">
          <div className="space-y-4">
             <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1.5 block">Election Title</label>
              <Input
                required
                placeholder="e.g. 2027 Board of Directors"
                value={newElectionTitle}
                onChange={(e) => setNewElectionTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1.5 block">Description</label>
              <Textarea
                required
                rows={4}
                placeholder="Details on what this election is for..."
                value={newElectionDescription}
                onChange={(e) => setNewElectionDescription(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1.5 block">Start Date</label>
                <Input type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1.5 block">End Date</label>
                <Input type="date" required value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 bg-blue-50 p-3 rounded-none border-2 border-black dark:border-white border-blue-100 flex items-center gap-2">
             <ShieldCheck size={16} className="text-blue-600 shrink-0" />
             <span>Elections created here will be securely encrypted. {getTerm('member')}s will be notified once the election opens.</span>
          </div>
          <div className="flex gap-3 justify-end pt-4 border-t border-black dark:border-white">
            <Button variant="secondary" type="button" onClick={() => setIsCreateElectionModalOpen(false)}>Cancel</Button>
            <Button 
              type="submit" 
              style={{ backgroundColor: brand.primary }}
              disabled={createElectionMutation.isPending}
            >
              {createElectionMutation.isPending ? 'Creating...' : 'Create Election'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
